import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface Registry {
  enter: () => void;
  leave: () => void;
}

const NO_REGISTRY: Registry = { enter: () => {}, leave: () => {} };

const RegistryContext = createContext<Registry>(NO_REGISTRY);
const BusyContext = createContext(false);

/**
 * Counts the price cells that are being edited or saved right now (R19):
 * the bulk bar must not apply while a cell still holds a draft or a save
 * is in flight, because the preview was computed on the values before
 * them. Two contexts so the cells only ever subscribe to the stable
 * register functions and only the bar re-renders when the count moves.
 */
export function PriceCellActivityProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [active, setActive] = useState(0);

  const enter = useCallback(() => setActive((count) => count + 1), []);
  const leave = useCallback(
    () => setActive((count) => Math.max(0, count - 1)),
    []
  );
  const registry = useMemo<Registry>(() => ({ enter, leave }), [enter, leave]);

  return (
    <RegistryContext.Provider value={registry}>
      <BusyContext.Provider value={active > 0}>{children}</BusyContext.Provider>
    </RegistryContext.Provider>
  );
}

/**
 * Registers a cell as busy while `busy` is true; a no-op outside the
 * provider. The cleanup leaves on unmount, so a row that disappears mid
 * edit (refetch, page change) never leaves a dangling count.
 */
export function usePriceCellActivity(busy: boolean): void {
  const { enter, leave } = useContext(RegistryContext);

  useEffect(() => {
    if (!busy) return;
    enter();
    return leave;
  }, [busy, enter, leave]);
}

/** True while any price cell on the page is editing or saving. */
export function usePriceCellsBusy(): boolean {
  return useContext(BusyContext);
}
